import { CSSResult, html, LitElement, nothing, PropertyValues, unsafeCSS } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { repeat } from 'lit/directives/repeat.js';
import { createRef, ref, Ref } from 'lit/directives/ref.js';
import type { Carousel } from '@breuni-ui/carousel';
import type { TeaserItemData } from '@cube/tracking-library-api';
import styles from './wishlist-brand-recommendations.scss?inline';
import { BrandMutationEvent, Messages, WishlistBrandRecommendation } from '../../../@types/wishlist.js';
import { WishlistAppConfig, wishlistAppConfigContext } from '../../../scripts/context/appConfigContext.js';
import { i18nContext } from '../../../scripts/context/i18nContext.js';
import { brandsContext } from '../../../scripts/context/wishlistContext.js';
import { BrandsState } from '../../../scripts/context/wishlist-state.js';
import { brandRecommendationsApi } from '../../../scripts/service/brand-recommendations-api.js';
import { trackTeaserClick, trackTeaserView } from '../../../scripts/service/cube-tracking-layer.js';
import { ADDED_BRAND_EVENT, config } from '../../../scripts/service/config.js';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';
import arrowLeft from '@breuni-ui/design-tokens/images/icons/o-ic-16_chevron-left.svg?element';
import arrowRight from '@breuni-ui/design-tokens/images/icons/o-ic-16_chevron-right.svg?element';
import { hideIconFromAssistiveTechnology } from '../icon/icon-accessibility.js';
import '../carousel/index.js';
import '../brand-follow-button/brand-follow-button.js';

const BRAND_TEASER_PROMOTION_DATA = {
  id: '',
  type: 'brand_slider',
  placement: 'bottom',
  module_position: 1,
  rule: 'brand_affinity_score',
  story: '',
} as const;

@customElement('ents-wishlist-brand-recommendations')
export class WishlistBrandRecommendationsElement extends LitElement {
  @consume({ context: wishlistAppConfigContext })
  private wishlistAppConfig!: WishlistAppConfig;

  @consume({ context: i18nContext })
  private messages!: Messages;

  @consume({ context: brandsContext, subscribe: true })
  private brandsState!: BrandsState;

  @state() private items: WishlistBrandRecommendation[] = [];

  @state() private itemsReady: boolean = false;

  @query('[data-ents-brand-reco-tracker]')
  private trackerElement?: HTMLElement;

  private carouselRef: Ref<Carousel> = createRef();

  private carouselResizeObserver?: ResizeObserver;

  private trackingItems: TeaserItemData[] = [];

  private isVisible: boolean = false;

  private viewSent: boolean = false;

  private observer?: IntersectionObserver;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles)];
  }

  protected firstUpdated(changedProperties: PropertyValues) {
    super.firstUpdated(changedProperties);
    if (!this.wishlistAppConfig.brandRecommendationsEnabled) return;
    document.addEventListener(ADDED_BRAND_EVENT, this.onBrandAdded);
    this.observeVisibility();
    void this.loadItems();
  }

  disconnectedCallback() {
    document.removeEventListener(ADDED_BRAND_EVENT, this.onBrandAdded);
    this.observer?.disconnect();
    this.carouselResizeObserver?.disconnect();
    super.disconnectedCallback();
  }

  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    this.observeCarouselResize();
  }

  private observeCarouselResize() {
    if (this.carouselResizeObserver || !this.carouselRef.value) return;
    // the vendor carousel measures its own offsetWidth once and bakes it into each tile's inline
    // style; items arrive asynchronously (after the recommendations fetch), so that one-shot
    // measurement can land before the surrounding layout has settled. Re-run it whenever the
    // carousel's rendered size actually changes, for any reason (window resize, a scrollbar
    // appearing, a late-loading web font reflowing the page, ...) instead of guessing at triggers.
    this.carouselResizeObserver = new ResizeObserver(() => this.carouselRef.value?.refresh());
    this.carouselResizeObserver.observe(this.carouselRef.value);
  }

  private async loadItems() {
    try {
      const items = await brandRecommendationsApi.listRecommendations(
        this.wishlistAppConfig.brandRecommendationsApiEndpoint,
      );
      this.items = items;
      this.trackingItems = this.buildTrackingItems(items);
      // only mark ready (and allow teaser_view to fire) on success, so a fetch failure isn't
      // recorded in analytics as indistinguishable from a genuine zero-recommendations customer
      this.itemsReady = true;
      this.maybeSendView();
    } catch (e) {
      console.error('failed to load brand recommendations', e);
    }
  }

  private buildTrackingItems(items: WishlistBrandRecommendation[]): TeaserItemData[] {
    return items.map((item, index) => ({
      item_type: 'Teaser',
      item_name: item.name,
      is_sponsored: false,
      position: index + 1,
      target_url: this.brandUrl(item),
    }));
  }

  private brandUrl(item: WishlistBrandRecommendation): string {
    return `${this.wishlistAppConfig.brandsUrl}${item.niceName}/`;
  }

  private observeVisibility() {
    if (!this.trackerElement || typeof IntersectionObserver === 'undefined') {
      this.isVisible = true;
      this.maybeSendView();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.isVisible = true;
            this.maybeSendView();
          }
        });
      },
      { threshold: [0.1] },
    );
    this.observer.observe(this.trackerElement);
  }

  private maybeSendView() {
    if (this.viewSent || !this.itemsReady || !this.isVisible || this.trackingItems.length === 0) return;
    this.viewSent = true;
    trackTeaserView({
      event_data: { is_interaction_event: false },
      items: this.trackingItems,
      promotion_data: {
        ...BRAND_TEASER_PROMOTION_DATA,
        name: this.messages['ents_wishlist_brand_recommendations_headline']!,
      },
      currency: { code: config.currency ?? '' },
    });
  }

  private handleTileClick(index: number) {
    const item = this.trackingItems[index];
    if (!item) return;
    trackTeaserClick({
      event_data: { is_interaction_event: true },
      items: [item],
      promotion_data: {
        ...BRAND_TEASER_PROMOTION_DATA,
        name: this.messages['ents_wishlist_brand_recommendations_headline']!,
      },
      currency: { code: config.currency ?? '' },
    });
  }

  private onBrandAdded = (e: Event) => {
    const code = (e as BrandMutationEvent).detail.brandCode;
    const item = this.items.find((b) => b.code === code);
    if (item && !this.brandsState.entities.some((b) => b.code === item.code)) {
      this.brandsState.add({ ...item, createdAt: new Date().toISOString() });
    }
  };

  private formatLogoAlt(brandName: string): string {
    const template = this.messages['ents_wishlist_brands_logo_alt'] ?? '{0}';
    return template.replace('{0}', brandName);
  }

  render() {
    if (!this.wishlistAppConfig.brandRecommendationsEnabled) return nothing;
    return html`
      ${this.items.length > 0 ? this.renderSlider() : nothing}
      <span data-ents-brand-reco-tracker aria-hidden="true"></span>
    `;
  }

  private renderSlider() {
    return html`
      <ents-carousel-container
        carousel-selector="ents-carousel"
        navigation-next-selector="ents-carousel-navi-next"
        navigation-prev-selector="ents-carousel-navi-prev"
        class="ents-wishlist-brand-recommendations"
        data-test-ents-wishlist-brand-recommendations
      >
        <header slot="header">
          <div class="container-center carousel-header">
            <div class="carousel-header--heading">
              <h2 class="ents-heading">${this.messages['ents_wishlist_brand_recommendations_headline']}</h2>
            </div>
            <div class="carousel-header--navigation">
              <ents-carousel-navi-prev>
                <button class="bdk-button-icon" size="extra-small" variant="outline">
                  ${hideIconFromAssistiveTechnology(arrowLeft())}
                  <span class="sr-only">${this.messages['ents_wishlist_brand_recommendations_slider_previous']}</span>
                </button>
              </ents-carousel-navi-prev>
              <ents-carousel-navi-next>
                <button class="bdk-button-icon" size="extra-small" variant="outline">
                  ${hideIconFromAssistiveTechnology(arrowRight())}
                  <span class="sr-only">${this.messages['ents_wishlist_brand_recommendations_slider_next']}</span>
                </button>
              </ents-carousel-navi-next>
            </div>
          </div>
        </header>
        <div slot="body">
          <ents-carousel class="ents-carousel" ${ref(this.carouselRef)}>
            ${repeat(
              this.items,
              (item) => item.code,
              (item, index) => this.renderTile(item, index),
            )}
          </ents-carousel>
        </div>
      </ents-carousel-container>
    `;
  }

  private renderTile(item: WishlistBrandRecommendation, index: number) {
    return html`
      <div
        class="ents-wishlist-brand-recommendations-tile"
        data-test-ents-wishlist-brand-recommendation-tile="${item.code}"
        @click="${{ handleEvent: () => this.handleTileClick(index), capture: true }}"
      >
        <a class="ents-wishlist-brand-recommendations-tile__link" href="${this.brandUrl(item)}">
          ${
            item.logo
              ? html`<img
                  class="ents-wishlist-brand-recommendations-tile__logo"
                  src="${item.logo}"
                  alt="${this.formatLogoAlt(item.name)}"
                  loading="lazy"
                />`
              : html`<span class="ents-wishlist-brand-recommendations-tile__name">${item.name}</span>`
          }
        </a>
        <span class="ents-wishlist-brand-recommendations-tile__label">${item.name}</span>
        <ents-brand-follow-button
          brand-code="${item.code}"
          brand-name="${item.name}"
          placement="brand_recommendation_slider"
        ></ents-brand-follow-button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ents-wishlist-brand-recommendations': WishlistBrandRecommendationsElement;
  }
}
